/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React from 'react';
import { Link } from 'react-router-dom';
// Redux Toolkit
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/userSlice';
import authRoles from '../../auth/authRoles';
// BASE components
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
// Material-UI components
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import moment from 'moment';

function ReviewsCard({ reviews, resource }) {
  // if there are no reviews, return null
  if (!reviews) {
    return null;
  }
  // get the current user from the Redux store
  const user = useSelector(selectUser);
  // define arrays for sorting reviews
  const currentReviews = [];
  const futureReviews = [];
  const pastReviews = [];
  
  // is this used?
  const now = new Date();
  
  // sort reviews by date
  const sortedReviews = reviews.sort(
    (a, b) => new Date(a.start) - new Date(b.start)
  );
  // loop through reviews and sort them into the appropriate arrays
  for (const review of sortedReviews) {
    // if the review is internal and the user is not a lead editor, skip it
    if (
      review.internality === true &&
      !authRoles.leadEditor.includes(user.role)
    )
      continue;
    // define today as the current date with time set to midnight
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // set the start and end dates to midnight and end of day respectively
    const startDate = new Date(review.start);
    startDate.setHours(0, 0, 0, 0);
    // set the end date to the end of the day
    const endDate = new Date(review.end);
    endDate.setHours(23, 59, 59, 999);
    // sort the review into the appropriate array based on the current date
    if (today < startDate) {
      futureReviews.push(review);
    } else if (today > endDate) {
      pastReviews.push(review);
    } else {
      currentReviews.push(review);
    }
  }
  // function to handle the review cycle name formatting
  const handleCycleName = (cycle) => {

    const name = cycle
      .split('_')
      .map((word, index) => {
        if (index === 0) {
          return word === 'dev' ? "Storyboard" : "Production";
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');

    return name;
  };
  // component to render the list of reviews
  const ReviewList = ({
    filteredReviews,
    type,
    isComplete = false
  }) => {

    const allCompleted = pastReviews.length === reviews.length;
    const emptyMessage = allCompleted ? 'All Reviews Completed.' : 'No ' + type + ' Reviews.';

    if (!filteredReviews || filteredReviews.length === 0) {
      return (
        <ListItem className="px-0">
          <ListItemText
            classes={{ primary: 'font-medium text-md' }}
            primary={
              <span className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-12">
                  <span className="flex items-center">
                    <BASESvgIcon size={20} className="mr-4">
                      {allCompleted
                          ? 'heroicons-outline:check'
                          : 'heroicons-outline:exclamation'}
                    </BASESvgIcon>
                    {emptyMessage}
                  </span>
              </span>
            }
          />
        </ListItem>
      );
    }

    return (
      <>
        {filteredReviews.map((review, index) => (
          <ListItem key={index} className={`p-0 ${index === 0 ? 'pt-8': ''}`}>
            <ListItemText
              classes={{ root: 'px-0', primary: 'font-medium text-md' }}
              primary={handleCycleName(review.cycle)}
              secondary={
                <span className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-12">
                  {review.start && (
                    <span className="flex items-center">
                      <BASESvgIcon size={20}>
                        {isComplete
                          ? 'heroicons-outline:check'
                          : 'heroicons-outline:calendar'}
                      </BASESvgIcon>
                      <Typography
                        component="span"
                        className="mx-6 text-md"
                        color="text.secondary"
                      >
                        {moment.utc(review.start).local().format('MMM DD, YYYY') +
                          ' '}
                        -
                        {' ' +
                          moment.utc(review.end).local().format('MMM DD, YYYY')}
                      </Typography>
                    </span>
                  )}
                </span>
              }
            />
          </ListItem>
        ))}
      </>
    );
  };

  return (
    <div className="w-full">
      <div className="mb-32 space-y-16">
        <Typography className="grow text-2xl font-bold tracking-tight leading-none py-8">
          Reviews
        </Typography>
        <Divider />
        <Paper className="flex flex-col flex-auto p-24 shadow rounded-xl overflow-hidden">
          <div className="flex items-center justify-between mb-24">
            <div className="flex flex-col">
              <Typography className="text-lg font-medium tracking-tight leading-6 truncate">
                {resource} Reviews
              </Typography>
              <Typography
                className="text-md font-medium"
                color="text.secondary"
              >
                Review cycles for {resource} instances
              </Typography>
            </div>
          </div>
          <List className="grid grid-cols-1 gap-24 p-0">
            <div>
              <Chip
                label="Active Reviews"
                color="secondary"
                size="small"
                className='mb-8'
              />
              <ReviewList filteredReviews={currentReviews} type="Active" />
            </div>
            <div>
              <Chip
                label="Upcoming Reviews"
                color="info"
                size="small"
                className='mb-8'
              />
              <ReviewList filteredReviews={futureReviews} type="Upcoming" />
            </div>
            <div>
              <Chip
                label="Completed Reviews"
                color="success"
                size="small"
                className='mb-8'
              />
              <ReviewList filteredReviews={pastReviews} type="Completed" isComplete />
            </div>
          </List>
        </Paper>
      </div>
    </div>
  );
}

export default ReviewsCard;
